import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";
import { Field, DayChips, SuccessCard, FormCard, SubmitButton } from "./kit";
import { VolunteerFields } from "./VolunteerFields";

const EMPTY = {
  name: "", email: "", phone: "", party_size: 1, days: [], opt_in: false, signature: "", acknowledged: false,
  volunteer: false, volunteer_role: "", volunteer_days: [], volunteer_notes: "", donate: false,
};

export const RegisterForm = ({ content, volunteerSignal = 0 }) => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggleVolunteer = (on) =>
    setF((p) => ({ ...p, volunteer: on, volunteer_days: on && !p.volunteer_days.length ? p.days : p.volunteer_days }));

  useEffect(() => {
    if (volunteerSignal) toggleVolunteer(true);
  }, [volunteerSignal]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.days.length) return setError("Please select at least one festival day.");
    if (f.volunteer && !f.volunteer_role) return setError("Please choose a preferred volunteer role.");
    if (f.volunteer && !f.volunteer_days.length) return setError("Please select at least one day you can volunteer.");
    if (!f.acknowledged) return setError("Please check the acknowledgment box.");
    setLoading(true);
    try {
      const payload = { ...f, party_size: Number(f.party_size) };
      if (!f.volunteer) {
        delete payload.volunteer_role;
        delete payload.volunteer_days;
        delete payload.volunteer_notes;
      }
      const { data } = await api.post("/registrations", payload);
      if (data.redirect_url) {
        setDone({ ...data, redirecting: true });
        toast.success("Registered — opening secure donation page");
        window.location.assign(data.redirect_url);
        return;
      }
      setDone({ ...data, name: data.name || f.name, party_size: data.party_size ?? Number(f.party_size), volunteer: data.volunteer ?? f.volunteer });
      toast.success("Registration received");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
        {done ? (
          <SuccessCard testid="register-success" title={`Ẹ ṣé, ${done.name}!`}>
            Your household of {done.party_size} is registered. We look forward to welcoming you at {content.location_name}.
            {done.volunteer && (
              <span className="mt-2 block text-emerald-300" data-testid="register-success-volunteer">
                Thank you for volunteering — the hosts will reach out with details before the festival.
              </span>
            )}
            {done.redirecting && (
              <span className="mt-2 block text-amber-300" data-testid="register-success-redirecting">
                Taking you to our secure Stripe donation page…{" "}
                <a href={done.redirect_url} className="underline">Continue</a>
              </span>
            )}
            {done.donation_error && (
              <span className="mt-2 block text-amber-300" data-testid="register-success-donation-error">{done.donation_error}</span>
            )}
          </SuccessCard>
        ) : (
          <FormCard>
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <Field label="Household / full name" id="reg-name" data-testid="register-name-input" required value={f.name} onChange={set("name")} placeholder="The Adeyemi Family" />
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Email" id="reg-email" type="email" data-testid="register-email-input" required value={f.email} onChange={set("email")} placeholder="you@example.com" />
                <Field label="Phone" id="reg-phone" type="tel" data-testid="register-phone-input" required value={f.phone} onChange={set("phone")} placeholder="(352) 555-0123" />
              </div>
              <Field label="Party size" id="reg-party" type="number" min={1} max={20} data-testid="register-party-size-input" required value={f.party_size} onChange={set("party_size")} hint="Including yourself (1–20)" />
              <Field label="Days attending">
                <DayChips value={f.days} onChange={set("days")} schedule={content.schedule} prefix="register" />
              </Field>
              <label className="flex cursor-pointer items-start gap-3 text-sm text-neutral-300">
                <Checkbox checked={f.opt_in} onCheckedChange={(v) => setF({ ...f, opt_in: !!v })} data-testid="register-optin-checkbox" className="mt-0.5 h-5 w-5" />
                Keep me informed about future Ilesa Ire events.
              </label>
              <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold text-emerald-300">
                <Checkbox checked={f.volunteer} onCheckedChange={(v) => toggleVolunteer(!!v)} data-testid="register-volunteer-checkbox" className="mt-0.5 h-5 w-5" />
                I'd like to volunteer
              </label>
              {f.volunteer && <VolunteerFields f={f} setF={setF} schedule={content.schedule} />}
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-neutral-200">
                <Checkbox checked={f.donate} onCheckedChange={(v) => setF({ ...f, donate: !!v })} data-testid="register-donate-checkbox" className="mt-0.5 h-5 w-5" />
                <span>
                  <span className="block font-semibold text-amber-300">Support the festival (min $5)</span>
                  <span className="text-xs text-neutral-400">After registering you'll continue to our secure Stripe page and choose your amount.</span>
                </span>
              </label>
              <div className="rounded-md border border-amber-500/30 bg-neutral-950 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">Festival acknowledgment</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-300" data-testid="register-ack-text">{content.acknowledgment_text}</p>
                <div className="mt-4 space-y-4">
                  <Field label="Type your full name (signature)" id="reg-sig" data-testid="register-signature-input" required value={f.signature} onChange={set("signature")} placeholder="Full name" className="field-input font-script text-lg" />
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-neutral-200">
                    <Checkbox checked={f.acknowledged} onCheckedChange={(v) => setF({ ...f, acknowledged: !!v })} data-testid="register-ack-checkbox" className="mt-0.5 h-5 w-5" />
                    I acknowledge
                  </label>
                </div>
              </div>
              {error && <p className="rounded-md border border-red-800 bg-red-950/50 p-3 text-sm text-red-300" data-testid="register-error">{error}</p>}
              <SubmitButton loading={loading} testid="register-submit-button">{f.donate ? "Register & Donate" : "Register Household"}</SubmitButton>
            </form>
          </FormCard>
        )}
    </>
  );
};
